
 import { createSlice, createAsyncThunk,  } from '@reduxjs/toolkit';
import { 
  collection, 
  query, 
  where, 
  Timestamp, 
  getCountFromServer, 
  getAggregateFromServer, 
  sum,
  doc,
  getDoc, 
  average 
} from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';

interface Metrics {
  attempted: string;
  missedCalls: number;
  contacted: number;
  inboundCalls: number;
  totalDuration: number;
  avgDuration: number;
}

interface CallMetricsState {
  metrics: Metrics;
  loading: boolean;
  error: string | null;
}

const initialState: CallMetricsState = {
  metrics: {
    attempted: "0/150",
    missedCalls: 0,
    contacted: 0,
    inboundCalls: 0,
    totalDuration: 0,
    avgDuration: 0,
  },
  loading: false,
  error: null
};

export const fetchCallMetrics = createAsyncThunk(
  'callMetrics/fetchCallMetrics',
  async (params: {
    startDate: Date;
    endDate: Date;
    userId?: string | null;
    userIds?: string[];
  }, { rejectWithValue, getState }) => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("No user authenticated");

      const userDocRef = doc(db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);
      const companyId = userDocSnap.data()?.companyId;

      if (!companyId) throw new Error("No company ID found");

      const callLogsRef = collection(db, `companies/${companyId}/callLogs`);
      const startTimestamp = Timestamp.fromDate(new Date(params.startDate));
      const endTimestamp = Timestamp.fromDate(new Date(params.endDate));

      let baseQuery = query(
        callLogsRef,
        where('callTimestamp', '>=', startTimestamp),
        where('callTimestamp', '<=', endTimestamp)
      );

      const state = getState() as any;
      const teamFilter = state.filter.selectedTeamFilter;
      const userIds = state.filter.userIds;
      const selectedUserId = state.filter.selectedUserId;

      console.log('teamFilter--------------------------------', teamFilter);
      console.log('userIds--------------------------------', userIds);
      console.log('selectedUserId--------------------------------', selectedUserId);
      

      if (teamFilter === "managed" && userIds.length > 0) {
        baseQuery = query(baseQuery, where('userId', 'in', userIds));
      } else if (teamFilter === 'individual' && selectedUserId) {
        baseQuery = query(baseQuery, where('userId', '==', selectedUserId));
      }

      const [attempted, missed, contacted, inbound] = await Promise.all([
        getCountFromServer(query(baseQuery, where('type', '==', 'OUTGOING'))),
        getCountFromServer(query(baseQuery, where('type', '==', 'MISSED'))),
        getCountFromServer(query(
          baseQuery,
          where('type', '==', 'OUTGOING'),
          where('duration', '>', 0)
        )),
        getCountFromServer(query(
          baseQuery,
          where('type', '==', 'INCOMING'),
          where('duration', '>', 0)
        ))
      ]);

      const durationStats = await getAggregateFromServer(baseQuery, {
        totalDuration: sum('duration'),
        avgDuration: average('duration')
      });

      const data = durationStats.data();
      
      return {
        attempted: `${attempted.data().count}/150`,
        missedCalls: missed.data().count,
        contacted: contacted.data().count,
        inboundCalls: inbound.data().count,
        totalDuration: Math.round((data.totalDuration || 0) / 60),
        avgDuration: Math.round((data.avgDuration || 0) / 60)
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Error fetching metrics');
    }
  }
);

const callMetricsSlice = createSlice({
  name: 'callMetrics',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCallMetrics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCallMetrics.fulfilled, (state, action) => {
        state.loading = false;
        state.metrics = action.payload;
      })
      .addCase(fetchCallMetrics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export default callMetricsSlice.reducer;